import { useEffect, useRef, useState, type FormEvent } from "react";
import { ApiRequestError, apiRequest } from "../api";
import { Dialog, Field, StatusPill } from "../components";
import type {
  SupportCenterResponse,
  SupportTicketMessage,
  SupportTicketReadResponse,
  SupportTicketThreadResponse,
} from "../types";

type RetryTarget = "center" | "thread" | null;

interface CloseTicketDialogTarget {
  ticketId: string;
  selectionVersion: number;
  subject: string;
  status: SupportTicketSummaryStatus;
}

export function SupportCenter() {
  const [center, setCenter] = useState<SupportCenterResponse["support"] | null>(null);
  const [thread, setThread] = useState<SupportTicketThreadResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadReloadVersion, setThreadReloadVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [closeDialogTarget, setCloseDialogTarget] =
    useState<CloseTicketDialogTarget | null>(null);
  const [error, setError] = useState("");
  const [retryTarget, setRetryTarget] = useState<RetryTarget>(null);
  const selectedIdRef = useRef(selectedId);
  const selectionVersion = useRef(0);
  const centerRequestVersion = useRef(0);
  const threadRequestVersion = useRef(0);

  const clearError = () => {
    setError("");
    setRetryTarget(null);
  };

  const selectTicketContext = (ticketId: string | null) => {
    selectionVersion.current += 1;
    threadRequestVersion.current += 1;
    selectedIdRef.current = ticketId;
    setSelectedId(ticketId);
    setThread(null);
    setReply("");
    setThreadLoading(ticketId !== null);
  };

  const selectTicketFromCurrentUrl = (
    support: SupportCenterResponse["support"],
  ) => {
    const ticketId = new URLSearchParams(window.location.search).get("ticket");
    const validId =
      ticketId && support.tickets.some((ticket) => ticket.id === ticketId)
        ? ticketId
        : null;
    if (ticketId !== validId) {
      writeTicketHistory(validId, true);
    }
    selectTicketContext(validId);
  };

  const writeTicketHistory = (ticketId: string | null, replace = false) => {
    const url = new URL(window.location.href);
    if (ticketId) {
      url.searchParams.set("ticket", ticketId);
    } else {
      url.searchParams.delete("ticket");
    }
    const target = `${url.pathname}${url.search}${url.hash}`;
    if (replace) {
      window.history.replaceState(null, "", target);
    } else {
      window.history.pushState(null, "", target);
    }
  };

  const loadCenter = async () => {
    const requestVersion = ++centerRequestVersion.current;
    const response = await apiRequest<SupportCenterResponse>(
      "/api/account/support-tickets",
    );
    if (requestVersion !== centerRequestVersion.current) {
      return null;
    }
    setCenter(response.support);
    return response.support;
  };

  useEffect(() => {
    let active = true;
    void loadCenter()
      .then((support) => {
        if (!active || !support) {
          return;
        }
        selectTicketFromCurrentUrl(support);
      })
      .catch((caught) => {
        if (active) {
          setError(caught instanceof Error ? caught.message : "无法读取客服工单");
          setRetryTarget("center");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
      centerRequestVersion.current += 1;
      threadRequestVersion.current += 1;
    };
  }, []);

  useEffect(() => {
    const onPopState = () => {
      const ticketId = new URLSearchParams(window.location.search).get("ticket");
      const validId =
        ticketId && center?.tickets.some((ticket) => ticket.id === ticketId)
          ? ticketId
          : null;
      if (selectedIdRef.current !== validId) {
        clearError();
        if (ticketId !== validId) {
          writeTicketHistory(validId, true);
        }
        selectTicketContext(validId);
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [center]);

  useEffect(() => {
    const requestVersion = ++threadRequestVersion.current;
    const ticketId = selectedId;
    if (!ticketId) {
      setThread(null);
      setThreadLoading(false);
      return;
    }
    let active = true;
    setThread(null);
    setReply("");
    setThreadLoading(true);
    void apiRequest<SupportTicketThreadResponse>(
      `/api/account/support-tickets/${ticketId}`,
    )
      .then((response) => {
        if (
          !active ||
          requestVersion !== threadRequestVersion.current ||
          selectedIdRef.current !== ticketId ||
          response.ticket.id !== ticketId
        ) {
          return;
        }
        setThread(response);
        if (response.ticket.unread) {
          void apiRequest<SupportTicketReadResponse>(
            `/api/account/support-tickets/${ticketId}/read`,
            {
              method: "POST",
              body: JSON.stringify({ lastAdminMessageAt: response.ticket.lastAdminMessageAt }),
            },
          )
            .then((readResponse) => {
              if (
                !active ||
                requestVersion !== threadRequestVersion.current ||
                selectedIdRef.current !== ticketId ||
                readResponse.ticket.id !== ticketId
              ) {
                return;
              }
              setThread((current) =>
                current?.ticket.id === ticketId
                  ? { ...current, ticket: readResponse.ticket }
                  : current,
              );
              setCenter((current) =>
                current
                  ? {
                      ...current,
                      tickets: current.tickets.map((ticket) =>
                        ticket.id === ticketId ? readResponse.ticket : ticket,
                      ),
                    }
                  : current,
              );
            })
            .catch(() => undefined);
        }
      })
      .catch((caught) => {
        if (
          active &&
          requestVersion === threadRequestVersion.current &&
          selectedIdRef.current === ticketId
        ) {
          setError(caught instanceof Error ? caught.message : "无法读取工单线程");
          setRetryTarget("thread");
        }
      })
      .finally(() => {
        if (
          active &&
          requestVersion === threadRequestVersion.current &&
          selectedIdRef.current === ticketId
        ) {
          setThreadLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [selectedId, threadReloadVersion]);

  const selectTicket = (ticketId: string) => {
    if (ticketId === selectedIdRef.current) {
      return;
    }
    clearError();
    selectTicketContext(ticketId);
    writeTicketHistory(ticketId);
  };

  const showCreateForm = () => {
    if (selectedIdRef.current === null) {
      return;
    }
    clearError();
    selectTicketContext(null);
    writeTicketHistory(null);
  };

  const retry = () => {
    clearError();
    if (retryTarget === "thread" && selectedIdRef.current) {
      setThreadReloadVersion((current) => current + 1);
      return;
    }
    setLoading(true);
    void loadCenter()
      .then((support) => {
        if (support) {
          selectTicketFromCurrentUrl(support);
        }
      })
      .catch((caught) => {
        setError(caught instanceof Error ? caught.message : "无法读取客服工单");
        setRetryTarget("center");
      })
      .finally(() => setLoading(false));
  };

  const createTicket = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    clearError();
    try {
      const response = await apiRequest<SupportTicketThreadResponse>(
        "/api/account/support-tickets",
        {
          method: "POST",
          body: JSON.stringify({ subject, message: newMessage }),
        },
      );
      setSubject("");
      setNewMessage("");
      selectTicketContext(response.ticket.id);
      setThread(response);
      writeTicketHistory(response.ticket.id);
      await loadCenter();
    } catch (caught) {
      setError(formatSupportError(caught));
    } finally {
      setBusy(false);
    }
  };

  const postReply = async (event: FormEvent) => {
    event.preventDefault();
    const operationTicketId = selectedIdRef.current;
    const operationVersion = selectionVersion.current;
    if (!operationTicketId || threadLoading) {
      return;
    }
    setBusy(true);
    clearError();
    try {
      const response = await apiRequest<SupportTicketThreadResponse>(
        `/api/account/support-tickets/${operationTicketId}/messages`,
        { method: "POST", body: JSON.stringify({ body: reply }) },
      );
      if (
        selectedIdRef.current === operationTicketId &&
        selectionVersion.current === operationVersion &&
        response.ticket.id === operationTicketId
      ) {
        setReply("");
        setThread(response);
      }
      await loadCenter();
    } catch (caught) {
      if (
        selectedIdRef.current === operationTicketId &&
        selectionVersion.current === operationVersion
      ) {
        setError(formatSupportError(caught));
      }
    } finally {
      setBusy(false);
    }
  };

  const openCloseTicketDialog = () => {
    const operationTicketId = selectedIdRef.current;
    if (!operationTicketId || threadLoading || busy || !thread) {
      return;
    }
    setCloseDialogTarget({
      ticketId: operationTicketId,
      selectionVersion: selectionVersion.current,
      subject: thread.ticket.subject,
      status: thread.ticket.status,
    });
  };

  const closeCloseTicketDialog = () => {
    if (!busy) {
      setCloseDialogTarget(null);
    }
  };

  const closeTicket = async () => {
    const target = closeDialogTarget;
    if (
      !target ||
      busy
    ) {
      return;
    }
    setBusy(true);
    clearError();
    try {
      await apiRequest(`/api/account/support-tickets/${target.ticketId}/close`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      await loadCenter();
      if (
        selectedIdRef.current === target.ticketId &&
        selectionVersion.current === target.selectionVersion
      ) {
        setThreadReloadVersion((current) => current + 1);
      }
    } catch (caught) {
      if (
        selectedIdRef.current === target.ticketId &&
        selectionVersion.current === target.selectionVersion
      ) {
        setError(formatSupportError(caught));
      }
    } finally {
      setBusy(false);
      setCloseDialogTarget(null);
    }
  };

  const canCreate =
    center !== null &&
    center.nextCreateAt === null &&
    center.openTicketCount < center.openTicketLimit;
  const selectedTicket = center?.tickets.find((ticket) => ticket.id === selectedId);

  if (loading) {
    return <div className="support-loading">正在读取客服工单…</div>;
  }

  return (
    <section className="support-center" aria-label="客服工单中心">
      <div className="support-center-heading">
        <div>
          <h2>工单</h2>
          <p>退款、找回或改授权仍走原来的流程。</p>
        </div>
        {center ? (
          <StatusPill tone={center.nextCreateAt ? "warn" : "good"}>
            {center.nextCreateAt ? `下次可新建：${formatDate(center.nextCreateAt)}` : "可以新建工单"}
          </StatusPill>
        ) : null}
      </div>

      {error ? (
        <div className="form-error" role="alert">
          {error}
          {retryTarget ? <button className="text-button" onClick={retry} type="button">重试加载</button> : null}
        </div>
      ) : null}

      <div className="support-layout">
        <aside className="support-list-panel" aria-label="工单列表">
          <div className="support-list-heading">
            <div><strong>我的工单</strong><span>{center?.tickets.length ?? 0}</span></div>
            <button className="secondary-button support-new-ticket" disabled={busy} onClick={showCreateForm} type="button">新建工单</button>
          </div>
          {center?.tickets.length ? (
            <div className="support-ticket-list">
              {center.tickets.map((ticket) => (
                <button
                  aria-current={selectedId === ticket.id ? "page" : undefined}
                  className={`support-ticket-list-item ${selectedId === ticket.id ? "active" : ""}`}
                  disabled={busy}
                  key={ticket.id}
                  onClick={() => selectTicket(ticket.id)}
                  type="button"
                >
                  <span>{ticket.unread ? <i aria-label="未读" className="support-unread-dot" /> : null}<strong>{ticket.subject}</strong></span>
                  <small>{statusLabel(ticket.status)} · {formatDate(ticket.lastMessageAt)}</small>
                </button>
              ))}
            </div>
          ) : <p className="support-empty">还没有工单。可以使用右侧表单创建第一条。</p>}
        </aside>

        <div className="support-workspace">
          {selectedId ? (
            <div className="support-mobile-ticket-context" aria-live="polite">
              <button className="text-button" disabled={busy} onClick={showCreateForm} type="button">返回工单列表</button>
              <span>当前工单：{selectedTicket?.subject ?? "正在打开工单"}</span>
            </div>
          ) : null}
          {threadLoading ? (
            <article className="support-thread support-thread-loading" aria-busy="true" aria-live="polite">正在加载工单线程…</article>
          ) : thread ? (
            <article className="support-thread">
              <div className="support-thread-heading">
                <div><span className="card-kicker">工单</span><h3>{thread.ticket.subject}</h3><small>{formatDate(thread.ticket.createdAt)}</small></div>
                <div className="support-thread-actions">
                  <StatusPill tone={thread.ticket.status === "CLOSED" ? "neutral" : "good"}>{statusLabel(thread.ticket.status)}</StatusPill>
                  {thread.ticket.status !== "CLOSED" ? <button className="secondary-button" disabled={busy || threadLoading} onClick={openCloseTicketDialog} type="button">关闭工单</button> : null}
                </div>
              </div>
              <div className="support-message-list">{thread.messages.map((message) => <SupportMessage key={message.id} message={message} />)}</div>
              {thread.ticket.status !== "CLOSED" ? (
                <form className="support-reply-form" onSubmit={postReply}>
                  <Field label="继续回复" hint="不要填写密码、验证码或密钥。">
                    <textarea disabled={busy || threadLoading} onChange={(event) => setReply(event.target.value)} placeholder="补充需要说明的内容" required rows={4} value={reply} />
                  </Field>
                  <button className="button" disabled={busy || threadLoading || !reply} type="submit">{busy ? "提交中…" : "发送回复"}</button>
                </form>
              ) : <p className="support-closed-note">这条工单已关闭。如需新的事项，请等待新建额度可用后创建新工单。</p>}
            </article>
          ) : (
            <CreateTicketForm canCreate={canCreate} center={center} busy={busy} message={newMessage} onMessageChange={setNewMessage} onSubjectChange={setSubject} onSubmit={createTicket} subject={subject} />
          )}
        </div>
      </div>
      <p className="support-safety-note">不要提交密码、暗号、验证码或证件。工单不是即时聊天。</p>
      <Dialog
        description={
          closeDialogTarget
            ? `将关闭“${closeDialogTarget.subject}”。当前状态：${statusLabel(closeDialogTarget.status)}。关闭后不能继续回复；如有新事项，请在额度可用时新建工单。`
            : undefined
        }
        dismissible={!busy}
        footer={
          <>
            <button className="button" disabled={busy} onClick={() => void closeTicket()} type="button">
              {busy ? "正在关闭…" : "确认关闭工单"}
            </button>
            <button className="secondary-button" disabled={busy} onClick={closeCloseTicketDialog} type="button">
              取消
            </button>
          </>
        }
        onClose={closeCloseTicketDialog}
        open={closeDialogTarget !== null}
        title="关闭这条工单？"
      />
    </section>
  );
}

function CreateTicketForm({ canCreate, center, busy, message, onMessageChange, onSubjectChange, onSubmit, subject }: {
  canCreate: boolean;
  center: SupportCenterResponse["support"] | null;
  busy: boolean;
  message: string;
  onMessageChange: (value: string) => void;
  onSubjectChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  subject: string;
}) {
  return <article className="support-create-card"><span className="card-kicker">新建</span><h3>创建工单</h3>
    {center?.nextCreateAt ? <p className="support-quota-warning">最近 24 小时已经创建过新工单，下次可在 {formatDate(center.nextCreateAt)} 后创建。现有工单仍可继续回复。</p> : center && center.openTicketCount >= center.openTicketLimit ? <p className="support-quota-warning">当前有 3 条未关闭工单，请先关闭一条，或继续回复现有工单。</p> : <p className="muted">每个账号 24 小时内最多新建 1 条，最多同时保留 3 条未关闭工单。</p>}
    <form onSubmit={onSubmit}>
      <Field label="主题" hint="最多 80 字"><input disabled={busy} onChange={(event) => onSubjectChange(event.target.value)} placeholder="例如：订单状态需要确认" required value={subject} /></Field>
      <Field label="问题描述" hint="最多 2000 字"><textarea disabled={busy} onChange={(event) => onMessageChange(event.target.value)} placeholder="请说明需要协助的事项。" required rows={7} value={message} /></Field>
      <button className="button" disabled={busy || !canCreate} type="submit">{busy ? "提交中…" : canCreate ? "创建工单" : "当前额度不可用"}</button>
    </form>
  </article>;
}

export function SupportMessage({ message }: { message: SupportTicketMessage }) {
  return <div className={`support-message ${message.authorType === "ADMIN" ? "admin" : "user"}`}><div className="support-message-meta"><strong>{message.authorType === "ADMIN" ? "CheemsPay 客服" : "我"}</strong><time dateTime={message.createdAt}>{formatDate(message.createdAt)}</time></div><p className="support-message-body">{message.body}</p></div>;
}

function statusLabel(status: SupportTicketSummaryStatus): string {
  switch (status) {
    case "WAITING_FOR_SUPPORT": return "等待客服回复";
    case "WAITING_FOR_USER": return "等待你的回复";
    case "CLOSED": return "已关闭";
  }
}

type SupportTicketSummaryStatus = "WAITING_FOR_SUPPORT" | "WAITING_FOR_USER" | "CLOSED";

function formatDate(value: string): string {
  return new Date(value).toLocaleString("zh-CN", { dateStyle: "short", timeStyle: "short" });
}

function formatSupportError(error: unknown): string {
  if (error instanceof ApiRequestError && error.status === 429) {
    return `${error.message} 请根据页面返回的重试时间稍后再试。`;
  }
  return error instanceof Error ? error.message : "客服工单操作失败，请稍后再试。";
}
